<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import VideoContainer from './VideoContainer.svelte';
	import WebRTCPlayer from './WebRTCPlayer.svelte';
	import RecordingIndicator from './RecordingIndicator.svelte';
	import CameraActions from '../DeviceDrawer/CameraActions.svelte';
	import { subscribeRecordingDuration } from '../helpers/recording-timer';
	import { computeCameraState, computeStreamState } from '../helpers/camera-state-helpers';
	import { getActiveStreamRecording, getCameraComponentKey } from '../helpers/camera-helpers';
	import type { CameraRecordings, CameraStreamer } from '../types/camera-status';
	import { PttUiState } from '../types/ptt-ui-state';

	export let device: Device;
	export let status: DeviceStatusG2;
	export let settings: DeviceSettingsG2;

	let recordingDuration = '';
	let durationCleanup: (() => void) | null = null;

	$: cameraKey = getCameraComponentKey(device);
	$: recordings = status?.[cameraKey]?.recordings as CameraRecordings | undefined;
	$: activeRecording = getActiveStreamRecording(recordings);
	$: recordingStartedAt = activeRecording ? activeRecording.ts * 1000 : null;

	$: {
		if (durationCleanup) durationCleanup();
		durationCleanup = subscribeRecordingDuration(recordingStartedAt, (d) => (recordingDuration = d));
	}

	$: isOnline = status?.online ?? false;
	$: isPrivacyMode = settings?.[cameraKey]?.privacy ?? false;
	$: streamer = (status?.[cameraKey] as { streamer?: CameraStreamer })?.streamer;
	// Unified camera-level block: privacy / offline / streamer not running.
	// When non-null, WebRTCPlayer must not be mounted.
	$: cameraState = computeCameraState(isPrivacyMode, isOnline, streamer);

	let streamLoading = true;
	let streamError = { heading: '', message: '' };
	let webrtcPlayer: WebRTCPlayer;
	let isZoomed = false;
	let pttState: PttUiState = PttUiState.UNAVAILABLE;

	$: streamState = computeStreamState(streamLoading, streamError);
	$: videoState = cameraState ?? streamState;
	$: canPlayStream = !cameraState;

	// The shell keeps this mounted and only slides it off-screen, so closing the
	// drawer never triggers `onDestroy`. Rather than infer the drawer state from
	// a store (which lags the real transition) watch the open class.
	let playerRoot: HTMLElement;

	onMount(() => {
		const panel = playerRoot?.closest('.drawer, .sidepanel');
		if (!panel) return;

		let wasOpen = panel.classList.contains('open');
		const observer = new MutationObserver(() => {
			const isOpen = panel.classList.contains('open');
			if (wasOpen && !isOpen) webrtcPlayer?.stop();
			wasOpen = isOpen;
		});
		observer.observe(panel, { attributes: true, attributeFilter: ['class'] });

		return () => observer.disconnect();
	});

	onDestroy(() => {
		if (durationCleanup) durationCleanup();
	});
</script>

<div class="player-root" bind:this={playerRoot}>
	<VideoContainer state={videoState} bind:isZoomed>
		{#if canPlayStream}
			<WebRTCPlayer
				bind:this={webrtcPlayer}
				deviceId={device.id}
				bind:isLoading={streamLoading}
				bind:errorMessage={streamError}
				bind:pttState
				showControls={!isZoomed}
			/>
		{/if}
		<RecordingIndicator
			slot="topLeft"
			visible={!!recordingStartedAt && canPlayStream}
			duration={recordingDuration}
		/>
		<CameraActions
			slot="actions"
			{device}
			bind:status
			bind:settings
			{pttState}
			{streamLoading}
			on:talkToggle={(e) => webrtcPlayer?.requestTalk(e.detail)}
		/>
	</VideoContainer>
</div>

<style>
	.player-root {
		display: contents;
	}
</style>
